import * as React from 'react';
import {
  Box,
  ImageMessage,
  HeadingText,
  ParagraphText,
  StackLayout,
} from '@leagueplatform/genesis-core';
import {
  type MasonryEngineNode,
  type MasonryEngineNodeRendererProps,
} from '@leagueplatform/masonry-engine';
import { useStaticAsset } from '@leagueplatform/asset-config';
import { BENEFITS_MODULE_ASSET_KEYS } from '../../../asset-map';

export const NODE_TYPE_BENEFITS_GENERIC_EMPTY_STATE =
  'benefitsGenericEmptyState';

type BenefitsGenericEmptyStateProps = {
  title: string;
  description?: string;
  image?: {
    source: string;
    description: string;
  };
  hasBorder?: boolean;
};

type BenefitsGenericEmptyStateNode = MasonryEngineNode<
  typeof NODE_TYPE_BENEFITS_GENERIC_EMPTY_STATE,
  BenefitsGenericEmptyStateProps,
  {},
  {}
>;

export type BenefitsGenericEmptyStateRendererProps =
  MasonryEngineNodeRendererProps<BenefitsGenericEmptyStateNode>;

export const BenefitsGenericEmptyStateRenderer = ({
  title,
  description,
  image,
  hasBorder = true,
}: BenefitsGenericEmptyStateRendererProps) => {
  const PLACEHOLDER = useStaticAsset(
    BENEFITS_MODULE_ASSET_KEYS.EMPTY_STATE_PLACEHOLDER,
  ) as string;
  const src = image?.source ?? PLACEHOLDER;
  const alt = image?.description ?? '';

  return (
    <Box
      css={{
        alignItems: 'center',
        borderStyle: hasBorder ? 'solid' : undefined,
        backgroundColor: '$surfaceBackgroundPrimary',
        borderColor: '$onSurfaceBorderSubdued',
        borderRadius: hasBorder ? '$large' : '$none',
        borderWidth: '$thin',
        display: 'flex',
        justifyContent: 'center',
        padding: '$two $oneAndHalf',
        width: '100%',
        textAlign: 'center',
      }}
    >
      <ImageMessage
        image={src}
        imageAlt={alt}
        imagePosition="top"
        imageHorizontalAlignment="center"
      >
        <StackLayout
          spacing="$half"
          css={{ justifyContent: 'center', alignItems: 'center' }}
        >
          <HeadingText level="2" size="lg">
            {title}
          </HeadingText>
          {description && <ParagraphText>{description}</ParagraphText>}
        </StackLayout>
      </ImageMessage>
    </Box>
  );
};
